/**
 * Sale product cart reminder popup.
 *
 * DAILY REMINDER: Shows once per day, after 15 seconds of cumulative time
 * (persisted across page navigations) with a sale product in the cart.
 * Once shown, it stays visible with a 5s minimum hold (no closing during
 * that window) and does not auto-hide. Not shown again until the next
 * calendar day (tracked via localStorage).
 */
(function () {
  if (window.__saleCartPopupInitialized) return;
  window.__saleCartPopupInitialized = true;

  var config = window.saleCartPopupSettings;
  if (!config || config.enabled === false) return;

  var applyToAllProducts = config.applyToAllProducts === true;
  if (!applyToAllProducts && (!Array.isArray(config.saleProductIds) || config.saleProductIds.length === 0)) return;

  var saleProductIdSet = {};
  (config.saleProductIds || []).forEach(function (id) {
    saleProductIdSet[String(id)] = true;
  });

  // =========================================================================
  // CONSTANTS
  // =========================================================================
  var DAILY_REMINDER_DELAY_MS = 15000;
  var ATC_THRESHOLD_MS = 60000;
  var MIN_VISIBLE_MS = 5000;
  var TICK_MS = 1000;
  var MAX_TICK_DELTA_MS = 5000;

  var STORAGE = {
    lastReminderDate: 'sale_popup_last_reminder_date',
    accumulatedMs: 'sale_popup_reminder_accumulated_ms',
    lastTickTs: 'sale_popup_reminder_last_tick_ts',
    shownToday: 'sale_popup_reminder_shown_today',
    firstSeenDate: 'sale_popup_reminder_first_seen_date',
    atcSessionActive: 'sale_popup_atc_session_active',
    atcAccumulatedMs: 'sale_popup_atc_accumulated_ms',
    atcLastTickTs: 'sale_popup_atc_last_tick_ts',
    atcShownInSession: 'sale_popup_atc_shown_in_session',
  };

  // =========================================================================
  // STORAGE HELPERS
  // =========================================================================
  function readStorage(key, fallback) {
    try {
      var value = window.localStorage.getItem(key);
      return value === null ? fallback : value;
    } catch (e) {
      return fallback;
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {}
  }

  function todayString() {
    var d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }

  function getReminderState() {
    return {
      accumulatedMs: parseInt(readStorage(STORAGE.accumulatedMs, '0'), 10) || 0,
      lastTickTs: parseInt(readStorage(STORAGE.lastTickTs, '0'), 10) || 0,
      shownToday: readStorage(STORAGE.shownToday, 'false') === 'true',
      firstSeenDate: readStorage(STORAGE.firstSeenDate, ''),
    };
  }

  function setReminderState(state) {
    writeStorage(STORAGE.accumulatedMs, String(state.accumulatedMs));
    writeStorage(STORAGE.lastTickTs, String(state.lastTickTs));
    writeStorage(STORAGE.shownToday, state.shownToday ? 'true' : 'false');
    writeStorage(STORAGE.firstSeenDate, state.firstSeenDate || '');
  }

  function getAtcState() {
    return {
      sessionActive: readStorage(STORAGE.atcSessionActive, 'false') === 'true',
      accumulatedMs: parseInt(readStorage(STORAGE.atcAccumulatedMs, '0'), 10) || 0,
      lastTickTs: parseInt(readStorage(STORAGE.atcLastTickTs, '0'), 10) || 0,
      shownInSession: readStorage(STORAGE.atcShownInSession, 'false') === 'true',
    };
  }

  function setAtcState(state) {
    writeStorage(STORAGE.atcSessionActive, state.sessionActive ? 'true' : 'false');
    writeStorage(STORAGE.atcAccumulatedMs, String(state.accumulatedMs));
    writeStorage(STORAGE.atcLastTickTs, String(state.lastTickTs));
    writeStorage(STORAGE.atcShownInSession, state.shownInSession ? 'true' : 'false');
  }

  // =========================================================================
  // PAGE DETECTION
  // =========================================================================
  function isCartOrCheckoutPage() {
    if (config.template === 'cart') return true;
    var path = window.location.pathname;
    return path.indexOf('/cart') === 0 || path.indexOf('/checkout') === 0;
  }

  // =========================================================================
  // CART INSPECTION
  // =========================================================================
  function hasSaleProduct(cartItems) {
    cartItems = cartItems || [];
    if (applyToAllProducts) return cartItems.length > 0;
    return cartItems.some(function (item) {
      return saleProductIdSet[String(item.product_id)];
    });
  }

  function fetchCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.ok ? res.json() : { items: [] };
      })
      .catch(function () {
        return { items: [] };
      });
  }

  // =========================================================================
  // POPUP DOM CONTROL
  // =========================================================================
  var popupEl = document.getElementById('sale-cart-popup');
  var ctaLink = document.getElementById('SaleCartPopupCta');
  var popupShownAt = 0;
  var popupListenersAttached = false;
  var reminderPopupShownThisLoad = false;
  var atcPopupShownThisLoad = false;

  function canClose() {
    return popupShownAt > 0 && (Date.now() - popupShownAt) >= MIN_VISIBLE_MS;
  }

  function openPopup() {
    if (!popupEl) return;
    popupEl.removeAttribute('hidden');
    popupShownAt = Date.now();

    if (!popupListenersAttached) {
      document.addEventListener('keydown', onEscape);
      document.addEventListener('click', onOutsideClick);
      popupListenersAttached = true;
    }
  }

  function showPopup() {
    if (reminderPopupShownThisLoad) return;
    reminderPopupShownThisLoad = true;
    openPopup();
  }

  function showAtcPopup() {
    if (atcPopupShownThisLoad) return;
    atcPopupShownThisLoad = true;
    openPopup();
  }

  function hidePopup() {
    if (!popupEl || popupEl.hasAttribute('hidden')) return;
    if (!canClose()) {
      // console.log('[SalePopup] Close blocked — minimum ' + (MIN_VISIBLE_MS / 1000) + 's hold not yet reached (' + ((Date.now() - popupShownAt) / 1000).toFixed(1) + 's elapsed)');
      return;
    }
    popupEl.setAttribute('hidden', 'hidden');
    document.removeEventListener('keydown', onEscape);
    document.removeEventListener('click', onOutsideClick);
  }

  function onEscape(evt) {
    if (evt.key === 'Escape') hidePopup();
  }

  function onOutsideClick(evt) {
    if (!popupEl || popupEl.hasAttribute('hidden')) return;
    if (popupEl.contains(evt.target)) return;
    hidePopup();
  }

  if (ctaLink) ctaLink.addEventListener('click', function () { hidePopup(); });

  // =========================================================================
  // DAILY REMINDER TIMER (accumulates 15s of time; persists across page nav)
  // =========================================================================
  var reminderTickInterval = null;
  var reminderActive = false;

  function reminderTick() {
    var state = getReminderState();
    if (state.shownToday) {
      stopReminderTimer();
      return;
    }

    var today = todayString();
    var isSameDayAsFirstSeen = state.firstSeenDate === today;

    if (isSameDayAsFirstSeen) {
      // console.log('[SalePopup] Daily reminder paused — sale product first seen today (' + state.firstSeenDate + '), eligible starting tomorrow');
      state.lastTickTs = Date.now();
      setReminderState(state);
      return;
    }

    var now = Date.now();
    var atcSessionActive = getAtcState().sessionActive;
    var eligiblePage = !isCartOrCheckoutPage() && !atcSessionActive;

    if (eligiblePage && document.visibilityState === 'visible') {
      var delta = now - (state.lastTickTs || now);
      if (delta > 0) {
        delta = Math.min(delta, MAX_TICK_DELTA_MS);
        state.accumulatedMs += delta;
      }
    }
    state.lastTickTs = now;
    setReminderState(state);

    // console.log('[SalePopup] Daily reminder timer: ' + (state.accumulatedMs / 1000).toFixed(1) + 's / ' + (DAILY_REMINDER_DELAY_MS / 1000) + 's | eligiblePage: ' + eligiblePage + (atcSessionActive ? ' (paused — ATC session active)' : ''));

    if (state.accumulatedMs >= DAILY_REMINDER_DELAY_MS) {
      if (eligiblePage) {
        // console.log('[SalePopup] Daily reminder threshold reached — showing popup');
        showPopup();
        state.shownToday = true;
        setReminderState(state);
        writeStorage(STORAGE.lastReminderDate, todayString());
        stopReminderTimer();
      }
    }
  }

  function startReminderTimer() {
    if (reminderTickInterval) return;
    reminderTickInterval = setInterval(reminderTick, TICK_MS);
  }

  function stopReminderTimer() {
    if (reminderTickInterval) {
      clearInterval(reminderTickInterval);
      reminderTickInterval = null;
    }
  }

  function syncReminderSession(hasSale) {
    var lastShown = readStorage(STORAGE.lastReminderDate, '');
    var today = todayString();
    var state = getReminderState();

    // Reset accumulated progress if the stored date rolled over to a new day.
    if (lastShown !== today && state.shownToday) {
      state.shownToday = false;
      state.accumulatedMs = 0;
      state.lastTickTs = Date.now();
      setReminderState(state);
    }

    if (hasSale && !state.firstSeenDate) {
      // Record the day the sale product first appeared in the cart. The
      // reminder is only eligible to fire on a LATER calendar day than this.
      state = getReminderState();
      state.firstSeenDate = today;
      setReminderState(state);
      // console.log('[SalePopup] Sale product first seen in cart today (' + today + ') — reminder eligible starting tomorrow');
    } else if (!hasSale && state.firstSeenDate) {
      // Sale product left the cart entirely — clear first-seen so a future
      // add starts the day-1 gate fresh.
      state = getReminderState();
      state.firstSeenDate = '';
      setReminderState(state);
    }

    if (hasSale && !reminderActive) {
      reminderActive = true;
      state = getReminderState();
      state.lastTickTs = Date.now();
      setReminderState(state);
    } else if (!hasSale && reminderActive) {
      reminderActive = false;
      stopReminderTimer();
      return;
    }

    if (hasSale) {
      state = getReminderState();
      if (!state.shownToday) {
        startReminderTimer();
      }
    }
  }

  // =========================================================================
  // CART RECHECK (triggers on page load and cart changes)
  // =========================================================================
  function recheckCart() {
    fetchCart().then(function (cart) {
      var hasSale = hasSaleProduct(cart.items);
      syncReminderSession(hasSale);
      maintainAtcSession(hasSale);
    });
  }

  // =========================================================================
  // ADD-TO-CART SESSION TIMER (60s cumulative browsing; independent of the
  // daily reminder above — own state, own storage keys, own triggers)
  // =========================================================================
  var atcTickInterval = null;

  function atcTick() {
    var state = getAtcState();
    if (!state.sessionActive || state.shownInSession) {
      stopAtcTimer();
      return;
    }

    var now = Date.now();
    var eligiblePage = !isCartOrCheckoutPage();

    if (eligiblePage) {
      var delta = now - (state.lastTickTs || now);
      if (delta > 0) {
        delta = Math.min(delta, MAX_TICK_DELTA_MS);
        state.accumulatedMs += delta;
      }
    }
    state.lastTickTs = now;
    setAtcState(state);

    // console.log('[SalePopup] ATC session timer: ' + (state.accumulatedMs / 1000).toFixed(1) + 's / ' + (ATC_THRESHOLD_MS / 1000) + 's | eligiblePage: ' + eligiblePage);

    if (state.accumulatedMs >= ATC_THRESHOLD_MS) {
      if (eligiblePage) {
        stopAtcTimer();
        // Verify the sale product is still actually in the cart right now
        // before displaying — the session may not have been ended yet if
        // removal happened without a tracked cart-update event.
        fetchCart().then(function (cart) {
          var stillHasSale = hasSaleProduct(cart.items);
          var finalState = getAtcState();
          finalState.shownInSession = true;
          finalState.sessionActive = false;
          setAtcState(finalState);

          if (stillHasSale) {
            // console.log('[SalePopup] ATC session threshold reached — showing popup');
            showAtcPopup();
          } else {
            // console.log('[SalePopup] ATC session threshold reached but sale product no longer in cart — popup suppressed');
          }
        });
      }
    }
  }

  function startAtcTimer() {
    if (atcTickInterval) return;
    atcTickInterval = setInterval(atcTick, TICK_MS);
  }

  function stopAtcTimer() {
    if (atcTickInterval) {
      clearInterval(atcTickInterval);
      atcTickInterval = null;
    }
  }

  // Maintenance only: ends an active session if the sale product is gone
  // or the shopper reached cart/checkout; otherwise keeps an already-active
  // session ticking. Never STARTS a new session — that only happens from an
  // explicit add-to-cart action, see startAtcSessionIfNeeded() below.
  function maintainAtcSession(hasSale) {
    var state = getAtcState();
    var onCartOrCheckout = isCartOrCheckoutPage();

    if ((!hasSale || onCartOrCheckout) && state.sessionActive) {
      // End the session if the sale product is removed OR the shopper
      // reaches the cart/checkout page. A new add-to-cart afterwards starts
      // a fresh session.
      state.sessionActive = false;
      state.shownInSession = false;
      state.accumulatedMs = 0;
      state.lastTickTs = Date.now();
      setAtcState(state);
      stopAtcTimer();
      return;
    }

    if (state.sessionActive && !state.shownInSession) {
      state.lastTickTs = Date.now();
      setAtcState(state);
      startAtcTimer();
    } else {
      stopAtcTimer();
    }
  }

  // Starts a brand new ATC session — only called from an actual "Add to
  // Cart" action (product-form submit, quick-add, quick-order-list), never
  // from a plain page load or cart-drawer quantity/removal update.
  function startAtcSessionIfNeeded(hasSale) {
    var state = getAtcState();
    var onCartOrCheckout = isCartOrCheckoutPage();

    if (hasSale && !onCartOrCheckout && !state.sessionActive) {
      state.sessionActive = true;
      state.shownInSession = false;
      state.accumulatedMs = 0;
      state.lastTickTs = Date.now();
      setAtcState(state);
      atcPopupShownThisLoad = false;
      startAtcTimer();
      // console.log('[SalePopup] ATC session started from add-to-cart action');
    }
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      var isAddToCartAction = event && event.source && event.source !== 'cart-items';
      if (isAddToCartAction) {
        fetchCart().then(function (cart) {
          startAtcSessionIfNeeded(hasSaleProduct(cart.items));
        });
      }
      recheckCart();
    });
  }
  document.addEventListener('cartUpdated', function () {
    recheckCart();
  });

  // =========================================================================
  // INITIALIZATION
  // =========================================================================
  recheckCart();
})();
